"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.IndexedDBStorage = void 0;
const error_1 = __importDefault(require("./error"));
class IndexedDBStorage {
    dbName;
    db;
    constructor(dbName) {
        this.dbName = dbName;
        this.db = null;
    }
    // Returns the current version of the database
    getVersion() {
        return this.db?.version || 0;
    }
    // Opens the IndexedDB connection and returns the database instance
    open() {
        return new Promise((resolve, reject) => {
            if (this.db) {
                resolve(this.db);
                return;
            }
            const request = indexedDB.open(this.dbName);
            request.onsuccess = () => {
                this.db = request.result;
                resolve(this.db);
            };
            request.onerror = () => {
                reject(request.error);
            };
        });
    }
    // Initializes the object store if it doesn't exist
    async init(storeName) {
        try {
            const db = await this.open();
            const existingStoreNames = Array.from(db.objectStoreNames);
            if (existingStoreNames.includes(storeName)) {
                return Promise.resolve();
            }
            const newVersion = this.getVersion() + 1;
            db.close();
            // Open the database with the new version to trigger the upgrade
            const upgradedDB = await new Promise((resolve, reject) => {
                const request = indexedDB.open(this.dbName, newVersion);
                request.onupgradeneeded = () => {
                    const upgradeDB = request.result;
                    // Create the new store if it doesn't exist
                    if (!upgradeDB.objectStoreNames.contains(storeName)) {
                        upgradeDB.createObjectStore(storeName);
                    }
                };
                request.onsuccess = () => {
                    resolve(request.result);
                };
                request.onerror = () => {
                    reject(new Error(error_1.default.INIT_ERROR));
                };
            });
            this.db = upgradedDB;
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    // Creates an index for the given object store if it doesn't already exist
    async createIndex(storeName, index) {
        try {
            const db = await this.open();
            const transaction = db.transaction([storeName], 'readonly');
            const objectStore = transaction.objectStore(storeName);
            if (objectStore.indexNames.contains(index.name)) {
                return Promise.resolve();
            }
            const currentVersion = this.getVersion();
            db.close();
            // Increment version to trigger the upgrade process
            const newVersion = currentVersion + 1;
            const upgradedDB = await new Promise((resolve, reject) => {
                const request = indexedDB.open(this.dbName, newVersion);
                request.onupgradeneeded = () => {
                    const upgradeDB = request.result;
                    if (upgradeDB.objectStoreNames.contains(storeName)) {
                        const transaction = request.transaction;
                        const objectStore = transaction.objectStore(storeName);
                        transaction.onerror = () => {
                            reject(new Error(error_1.default.CREATE_INDEX_ERROR));
                        };
                        // Create the index if it doesn't exist
                        if (!objectStore.indexNames.contains(index.name)) {
                            objectStore.createIndex(index.name, index.keyPath, index.options || {});
                        }
                    }
                };
                request.onsuccess = () => {
                    resolve(request.result);
                };
                request.onerror = (event) => {
                    console.error(`Error creating index on store ${storeName}:`, event);
                    reject(new Error(error_1.default.CREATE_INDEX_ERROR));
                    transaction.abort();
                };
            });
            this.db = upgradedDB;
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    // Inserts or updates a record in the object store
    async put(arg) {
        try {
            const db = await this.open();
            const storeName = arg.getStoreName();
            const transaction = db.transaction([storeName], 'readwrite');
            const store = transaction.objectStore(storeName);
            const request = store.put(arg.toJSON(), arg.getKey());
            await new Promise((resolve, reject) => {
                request.onsuccess = () => {
                    transaction.commit();
                };
                request.onerror = () => {
                    console.error(`Failed to put item with key ${arg.getKey()} into store ${storeName}`);
                    reject(new Error(error_1.default.PUT_ERROR));
                    transaction.abort();
                };
                transaction.oncomplete = () => {
                    resolve();
                };
                transaction.onerror = () => {
                    reject(new Error(error_1.default.PUT_ERROR));
                };
            });
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    // Inserts or updates all passed records in the object store
    async putAll(args) {
        try {
            const db = await this.open();
            const storeName = args[0].getStoreName();
            const transaction = db.transaction([storeName], 'readwrite');
            const store = transaction.objectStore(storeName);
            try {
                for await (const t of args) {
                    const request = store.put(t.toJSON(), t.getKey());
                    await new Promise((resolve, reject) => {
                        request.onsuccess = () => {
                            resolve();
                        };
                        request.onerror = () => {
                            console.error(`Failed to put item with key ${t.getKey()} into store ${storeName}`);
                            reject(new Error(error_1.default.PUT_ERROR));
                        };
                    });
                }
            }
            catch (e) {
                transaction.abort();
                throw e;
            }
            transaction.commit();
            await new Promise((resolve, reject) => {
                transaction.oncomplete = () => {
                    resolve();
                };
                transaction.onerror = () => {
                    reject(new Error(error_1.default.PUT_ERROR));
                };
            });
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    // Retrieves a record by its key from the object store
    async get(storeName, key) {
        try {
            const db = await this.open();
            const transaction = db.transaction([storeName], 'readonly');
            const store = transaction.objectStore(storeName);
            const result = await new Promise((resolve, reject) => {
                const request = store.get(key);
                request.onsuccess = () => {
                    resolve(request.result);
                };
                request.onerror = () => {
                    console.error(`Error retrieving data from store ${storeName}`);
                    reject(new Error(error_1.default.GET_ERROR));
                    transaction.abort();
                };
                transaction.onerror = () => {
                    reject(new Error(error_1.default.GET_ERROR));
                };
            });
            return result;
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    // Retrieves all records in range using an index from the object store
    async getAllFromIndexInRange(indexName, lower, upper, lowerExclusive = false, upperExclusive = false) {
        try {
            const db = await this.open();
            const transaction = db.transaction([this.dbName], 'readonly');
            const store = transaction.objectStore(this.dbName);
            const index = store.index(indexName);
            const range = IDBKeyRange.bound(lower, upper, lowerExclusive, upperExclusive);
            const out = [];
            await new Promise((resolve, reject) => {
                const r = index.openCursor(range);
                r.onsuccess = (event) => {
                    if (!event.target) {
                        return;
                    }
                    const cursor = event.target.result;
                    if (cursor) {
                        out.push(cursor.value);
                        cursor.continue();
                    }
                    else {
                        resolve();
                    }
                };
                r.onerror = () => {
                    console.error(`Error retrieving data using index ${indexName} on store ${this.dbName}`);
                    reject(new Error(error_1.default.GET_ERROR));
                    transaction.abort();
                };
                transaction.onerror = () => {
                    reject(new Error(error_1.default.GET_ERROR));
                };
            });
            return out;
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    // Retrieves a record using an index from the object store
    async getFromIndex(storeName, indexName, key) {
        try {
            const db = await this.open();
            const transaction = db.transaction([storeName], 'readonly');
            const store = transaction.objectStore(storeName);
            const index = store.index(indexName);
            const result = await new Promise((resolve, reject) => {
                const request = index.get(key);
                request.onsuccess = () => {
                    resolve(request.result);
                };
                request.onerror = () => {
                    console.error(`Error retrieving data using index ${indexName} on store ${storeName}`);
                    reject(new Error(error_1.default.GET_ERROR));
                    transaction.abort();
                };
                transaction.onerror = () => {
                    reject(new Error(error_1.default.GET_ERROR));
                };
            });
            return result;
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    // Retrieves all records from the object store
    async getAll(storeName) {
        try {
            const db = await this.open();
            const transaction = db.transaction([storeName], 'readonly');
            const store = transaction.objectStore(storeName);
            const results = await new Promise((resolve, reject) => {
                const request = store.getAll();
                request.onsuccess = () => {
                    const result = request.result;
                    if (result && result.length > 0) {
                        resolve(result);
                    }
                    else {
                        resolve(undefined);
                    }
                };
                request.onerror = () => {
                    console.error(`Error retrieving all data from store ${storeName}`);
                    reject(new Error(error_1.default.GET_ERROR));
                    transaction.abort();
                };
                transaction.onerror = () => {
                    reject(new Error(error_1.default.GET_ERROR));
                };
            });
            return results;
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    // Deletes a record from the object store by its key
    async delete(arg) {
        try {
            const db = await this.open();
            const storeName = arg.getStoreName();
            const transaction = db.transaction([storeName], 'readwrite');
            const store = transaction.objectStore(storeName);
            await new Promise((resolve, reject) => {
                const request = store.delete(arg.getKey());
                request.onsuccess = () => {
                    transaction.commit();
                };
                request.onerror = () => {
                    console.error(`Failed to delete item with key ${arg.getKey()} from store ${storeName}`);
                    reject(new Error(error_1.default.DELETE_ERROR));
                    transaction.abort();
                };
                transaction.oncomplete = () => {
                    resolve();
                };
                transaction.onerror = () => {
                    reject(new Error(error_1.default.DELETE_ERROR));
                };
            });
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    // Deletes multiple records from the object store
    async deleteBatch(args) {
        if (args.length === 0) {
            return Promise.resolve();
        }
        const storeName = args[0].getStoreName();
        try {
            const db = await this.open();
            const transaction = db.transaction([storeName], 'readwrite');
            const store = transaction.objectStore(storeName);
            const deletePromises = args.map(arg => {
                return new Promise((resolve, reject) => {
                    const request = store.delete(arg.getKey());
                    request.onsuccess = () => {
                        resolve();
                    };
                    request.onerror = () => {
                        console.error(`Failed to delete item with key ${arg.getKey()} from store ${storeName}`);
                        transaction.abort();
                        reject(new Error(error_1.default.DELETE_ERROR));
                    };
                });
            });
            // Await all the delete operations concurrently
            await Promise.all(deletePromises);
            transaction.commit();
            await new Promise((resolve, reject) => {
                transaction.oncomplete = () => {
                    resolve();
                };
                transaction.onerror = () => {
                    reject(new Error(error_1.default.DELETE_ERROR));
                };
            });
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    // Deletes multiple records from the object store
    async deleteBatchKeys(keys) {
        if (keys.length === 0) {
            return Promise.resolve();
        }
        const storeName = this.dbName;
        try {
            const db = await this.open();
            const transaction = db.transaction([storeName], 'readwrite');
            const store = transaction.objectStore(storeName);
            try {
                for await (const k of keys) {
                    const request = store.delete(k);
                    await new Promise((resolve, reject) => {
                        request.onsuccess = () => {
                            resolve();
                        };
                        request.onerror = () => {
                            console.error(`Failed to delete item with key ${k} into store ${storeName}`);
                            reject(new Error(error_1.default.DELETE_ERROR));
                        };
                    });
                }
            }
            catch (e) {
                transaction.abort();
                throw e;
            }
            transaction.commit();
            await new Promise((resolve, reject) => {
                transaction.oncomplete = () => {
                    resolve();
                };
                transaction.onerror = () => {
                    reject(new Error(error_1.default.DELETE_ERROR));
                };
            });
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    async getTotalEntityCount() {
        const db = await this.open();
        const transaction = db.transaction([this.dbName], 'readonly');
        const store = transaction.objectStore(this.dbName);
        const request = store.count();
        return await new Promise((resolve, reject) => {
            request.onsuccess = () => {
                resolve(request.result);
            };
            request.onerror = () => {
                console.error(`Failed to get entity count of store ${this.dbName}`);
                reject(new Error(error_1.default.GENERAL));
            };
        });
    }
    // Deletes records up to numberOfEntriesToDelete using the ascending order of an index from the object store, returning number of deleted entities.
    // Maximum of 10000 deletes in one call.
    async deleteFromIndexAscUntilSize(indexName, numberOfEntriesToDelete) {
        const maxLength = Math.min(numberOfEntriesToDelete, 10000);
        try {
            const db = await this.open();
            const transaction = db.transaction([this.dbName], 'readonly');
            const store = transaction.objectStore(this.dbName);
            const index = store.index(indexName);
            const toDelete = [];
            await new Promise((resolve, reject) => {
                const r = index.openCursor(); // Delete ascending
                r.onsuccess = (event) => {
                    if (!event.target) {
                        return;
                    }
                    const cursor = event.target.result;
                    if (cursor && toDelete.length < maxLength) {
                        toDelete.push(cursor.primaryKey);
                        cursor.continue();
                    }
                    else {
                        resolve();
                    }
                };
                r.onerror = () => {
                    console.error(`Error retrieving data using index ${indexName} on store ${this.dbName}`);
                    reject(new Error(error_1.default.GET_ERROR));
                    transaction.abort();
                };
                transaction.onerror = () => {
                    reject(new Error(error_1.default.GET_ERROR));
                };
            });
            await this.deleteBatchKeys(toDelete);
            return toDelete.length;
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
}
exports.IndexedDBStorage = IndexedDBStorage;
